<script>
  // TO FIX:
  // Map seems to crop from the left-hand side on mobile
  // Maybe drop shadow on minimap?
  // Go through charts CSS and update to use Guardian Source variables
  // Add a loading state to the map

  // Core imports
  import { onMount, tick } from "svelte"
  import { createEventDispatcher } from "svelte"
  import { getJson } from "$lib/helpers/guardian/toolbelt.js"
  // Guardian approved colour palettes
  import {
    categoricalLight,
    categoricalDark,
  } from "$lib/helpers/guardian/colours"
  import maplibregl from "maplibre-gl"
  import "maplibre-gl/dist/maplibre-gl.css"
  const { Map, ScaleControl } = maplibregl
  import { geoMercator, geoPath } from "d3-geo"
  import { select } from "d3-selection"
  import * as d3 from "d3"

  import basemap from "$lib/mapstyles/basemap-styles.json"
  import aus from "$lib/mapstyles/aus-simple.json"
  import rainCsv from "$assets/rain.csv?raw"
  // Add darkmode detection here at some point

  // Example for getting chart data from a store
  //import {getExampleData, exampledata} from '$lib/stores/example.svelte.js';

  let width = $state(620)
  let height = 500

  // Toggle to disable map interactions (set to false to disable zoom/pan)

  // For debugging

  // const centerPoint = {
  //       'type': 'FeatureCollection',
  //       'features': [
  //           {
  //               'type': 'Feature',
  //               'geometry': {
  //                   'type': 'Point',
  //                   'coordinates': center
  //               }
  //           }
  //       ]
  //   };
  // const centerStyle = {
  //   "id":"center-point",
  //   "type":"circle",
  //   "source":"center-point",
  //   "paint":{
  //     "circle-radius":10,
  //     "circle-color":"#000"
  //   }
  // }

  // GeoJSON styles are now provided by parent components (e.g., Atom.svelte)

  // Component props
  let {
    geoJsonURL = "https://interactive.guim.co.uk/2026/01/aus-fire-map/VIC-warning.json",
    geoJsonStyles = [],
    MAP_INTERACTIVE = true,
    SHOW_NAVIGATION_CONTROL = false,
    SHOW_SCALE_CONTROL_BOTTOM_LEFT = false,
    SHOW_FULLSCREEN_CONTROL = false,
    SHOW_GEOLOCATE_CONTROL = false,
    center = [116.03196265904751, -31.90047341428921],
    zoom = 8,
    headline = "",
    subtitle = "",
    source = "",
  } = $props()

  let mapInstance
  let minimapSvg
  let minimapPath
  let viewportRect
  let minimapProjection
  let timestamp = $state(null)
  let isLoading = $state(true)
  let currentDate = $state(null)
  let valueMin = $state(null)
  let valueMax = $state(null)
  let scaleLabels = $state(["0", "0.2–9", "10–24", "25–49", "50–99", "100+"])
  const dispatch = createEventDispatcher()

  // Single source of truth: D3 threshold scale for rainfall colours
  // Update colours here and both map + key update automatically
  const rainfallScale = d3
    .scaleThreshold()
    .domain([0.2, 10, 25, 50, 100])
    .range([
      "#F678BB", "#CBA36E", "#A1A1A1", "#23B4A9", "#0094DA", "#C70000"
    ])


    


  const keyGradientStyle = rainfallScale
    .range()
    .map(
      (color, i, arr) =>
        `${color} ${(i / arr.length) * 100}%, ${color} ${((i + 1) / arr.length) * 100}%`,
    )
    .join(", ")

  // Function to update minimap viewport rectangle
  function updateMinimap() {
    if (!mapInstance || !minimapPath || !viewportRect) return

    try {
      // Get current map bounds from the main MapLibre map
      const bounds = mapInstance.getBounds()
      const sw = bounds.getSouthWest() // returns {lng, lat}
      const ne = bounds.getNorthEast() // returns {lng, lat}

      // Validate bounds are reasonable (within Australia's approximate bounds)
      if (sw.lng < 100 || ne.lng > 160 || sw.lat < -50 || ne.lat > 0) {
        viewportRect.attr("d", null) // Hide if bounds are invalid
        return
      }

      // Create GeoJSON polygon for viewport bounds
      // D3 expects clockwise exterior rings (area to the right of boundary)
      // Order: SW -> NW -> NE -> SE -> SW (clockwise)
      const viewportGeo = {
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: [
            [
              [sw.lng, sw.lat], // SW
              [sw.lng, ne.lat], // NW
              [ne.lng, ne.lat], // NE
              [ne.lng, sw.lat], // SE
              [sw.lng, sw.lat], // close back to SW
            ],
          ],
        },
      }

      // Use minimapPath to generate the path
      const pathData = minimapPath(viewportGeo)
      if (pathData) {
        // Calculate viewport size to adjust stroke width for small rectangles
        const lonRange = ne.lng - sw.lng
        const latRange = ne.lat - sw.lat
        const area = lonRange * latRange

        // Increase stroke width when viewport is small (threshold is arbitrary, adjust as needed)
        const strokeWidth = area < 0.5 ? 4 : area < 2 ? 3 : 2

        viewportRect
          .attr("d", pathData)
          .attr("stroke-width", strokeWidth)
          .style("display", "block")
      } else {
        viewportRect.style("display", "none")
      }
    } catch (e) {
      // Silently handle errors
    }
  }

  // Watch for width changes and resize map
  $effect(() => {
    // Track width changes
    width
    if (mapInstance && mapInstance.loaded()) {
      // Wait for DOM to update, then resize the map
      tick().then(() => {
        // Use double requestAnimationFrame to ensure browser has painted
        // and container dimensions are updated
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            mapInstance.resize()
          })
        })
      })
    }
  })

  // Component lifecycle
  onMount(async () => {
    // Load rain CSV, parse and sort by date (bundled at build time)
    const rainRaw = d3.csvParse(rainCsv, (row) => ({
      date: row.date,
      value: +row.value,
      lat: +row.Lat,
      lng: +row.Long,
      siteName: row.SiteName,
      state: row.State,
    }))
    const rainData = rainRaw.sort((a, b) => a.date.localeCompare(b.date))
    const byDate = d3.group(rainData, (d) => d.date)
    const dates = [...byDate.keys()]

    // Compute bounds and value range from rain data
    const rainBounds =
      rainData.length > 0
        ? [
            [d3.min(rainData, (d) => d.lng), d3.min(rainData, (d) => d.lat)],
            [d3.max(rainData, (d) => d.lng), d3.max(rainData, (d) => d.lat)],
          ]
        : null
    const minValue = rainData.length > 0 ? d3.min(rainData, (d) => d.value) : 0
    const maxValue = rainData.length > 0 ? d3.max(rainData, (d) => d.value) : 0
    valueMin = minValue
    valueMax = maxValue

    // Build MapLibre step expression from D3 scale
    const rainfallColorScale = [
      "step",
      ["get", "value"],
      rainfallScale.range()[0],
      ...rainfallScale
        .domain()
        .flatMap((t, i) => [t, rainfallScale.range()[i + 1]]),
    ]

    // Conditionally fetch GeoJSON if a URL is provided
    let overlaysSource = null
    let combinedLayers = [...basemap]

    if (geoJsonURL && geoJsonURL.trim() !== "") {
      try {
        const geojson = await getJson(geoJsonURL)
        overlaysSource = {
          type: "geojson",
          data: geojson,
        }
        combinedLayers = [...basemap, ...geoJsonStyles]
      } catch (e) {
        // If fetching fails, fall back to basemap only
        overlaysSource = null
        combinedLayers = [...basemap]
      }
    }

    // Build GeoJSON for a given date (MapLibre uses [lng, lat])
    function pointsToGeoJson(points) {
      return {
        type: "FeatureCollection",
        features: points.map((p) => ({
          type: "Feature",
          geometry: { type: "Point", coordinates: [p.lng, p.lat] },
          properties: { value: p.value, siteName: p.siteName },
        })),
      }
    }

    const rainSource = {
      type: "geojson",
      data:
        dates.length > 0
          ? pointsToGeoJson(byDate.get(dates[0]))
          : { type: "FeatureCollection", features: [] },
    }

    const rainLayer = {
      id: "rain-points",
      type: "circle",
      source: "rain-points",
      paint: {
        "circle-radius": [
          "interpolate",
          ["linear"],
          ["zoom"],
          4,
          3,
          6,
          4,
          8,
          5,
          10,
          7,
          12,
          10,
        ],
        "circle-color": rainfallColorScale,
        "circle-opacity": 0.8,
        "circle-stroke-width": 1,
        "circle-stroke-color": "#fff",
      },
    }

    let mapDefs = {
      version: 8,
      sources: (() => {
        const sources = {
          "vector-tiles": {
            type: "vector",
            tiles: [
              "https://interactive.guim.co.uk/maptiles/world/{z}/{x}/{y}.pbf",
            ],
          },
          "rain-points": rainSource,
        }
        if (overlaysSource) {
          sources["overlays"] = overlaysSource
        }
        return sources
      })(),
      sprite: "",
      glyphs:
        "https://interactive.guim.co.uk/maptiles/fonts/{fontstack}/{range}.pbf",
      layers: [...combinedLayers, rainLayer],
    }

    mapInstance = new Map({
      container: "cartoMap",
      style: mapDefs,
      center: center,
      zoom: zoom,
      attributionControl: false,
      maplibreLogo: false,
      cooperativeGestures: width < 480 ? true : false,
    })

    // Disable interactions if MAP_INTERACTIVE is false
    if (!MAP_INTERACTIVE) {
      mapInstance.dragPan.disable()
      mapInstance.scrollZoom.disable()
      mapInstance.boxZoom.disable()
      mapInstance.doubleClickZoom.disable()
      mapInstance.touchZoomRotate.disable()
    }

    if (SHOW_NAVIGATION_CONTROL) {
      mapInstance.addControl(new maplibregl.NavigationControl(), "top-right")
    }
    if (SHOW_SCALE_CONTROL_BOTTOM_LEFT) {
      mapInstance.addControl(
        new maplibregl.ScaleControl({ maxWidth: 120, unit: "metric" }),
        "bottom-left",
      )
    }
    if (SHOW_FULLSCREEN_CONTROL) {
      mapInstance.addControl(new maplibregl.FullscreenControl(), "top-right")
    }
    if (SHOW_GEOLOCATE_CONTROL) {
      mapInstance.addControl(
        new maplibregl.GeolocateControl({
          positionOptions: { enableHighAccuracy: true },
        }),
        "top-right",
      )
    }

    mapInstance.addControl(
      new ScaleControl({ unit: "metric" }),
      "bottom-right",
    )

    // Set up minimap
    const minimapWidth = 150
    const minimapHeight = 100

    // Use identity projection for Web Mercator coordinates
    minimapProjection = geoMercator().fitExtent(
      [
        [0, 0],
        [minimapWidth, minimapHeight],
      ],
      aus,
    )

    minimapPath = geoPath().projection(minimapProjection)

    // Create SVG for minimap
    minimapSvg = select("#minimap")
      .append("svg")
      .attr("width", minimapWidth)
      .attr("height", minimapHeight)

    minimapSvg
      .append("g")
      .selectAll("path")
      .data(aus.features)
      .enter()
      .append("path")
      .attr("d", minimapPath)
      .attr("fill", "#FFF")
      .attr("stroke", "#000")
      .attr("stroke-width", 1)
      .attr("fill-rule", "evenodd")

    // Add viewport rectangle (on top of Australia)
    viewportRect = minimapSvg
      .append("path")
      .attr("fill", "rgba(255, 0, 0, 0.6)")
      .attr("stroke", "#c70000")
      .attr("stroke-width", 2)

    // Function to log map center and zoom
    function logMapState() {
      const center = mapInstance.getCenter()
      const zoom = mapInstance.getZoom()
      //console.log(`${center.lng}, ${center.lat}, 'Zoom:', ${zoom}`)
    }

    let rainTimeout

    // Function to set up map after it loads
    function setupMapAfterLoad() {
      updateMinimap() // Initial update
      // Listen to map movement and zoom events
      mapInstance.on("move", () => {
        updateMinimap()
        logMapState()
      })
      mapInstance.on("moveend", updateMinimap)
      mapInstance.on("zoom", () => {
        updateMinimap()
        logMapState()
      })
      mapInstance.on("zoomend", updateMinimap)
      mapInstance.on("resize", updateMinimap)
      isLoading = false

      // Animate through dates: 2 seconds per day, looping
      let dateIndex = 0
      currentDate = dates[0] ?? null

      function advanceDate() {
        if (dates.length === 0) return
        dateIndex = (dateIndex + 1) % dates.length
        const dateStr = dates[dateIndex]
        currentDate = dateStr
        const points = byDate.get(dateStr)
        const source = mapInstance.getSource("rain-points")
        if (source) {
          source.setData(pointsToGeoJson(points))
        }
        rainTimeout = setTimeout(advanceDate, 2000)
      }

      rainTimeout = setTimeout(advanceDate, 2000)
    }

    // Check if map is already loaded (can happen in article format)
    if (mapInstance.loaded()) {
      setupMapAfterLoad()
    } else {
      mapInstance.once("load", setupMapAfterLoad)
    }

    mapInstance.on("load", () => {
      mapInstance.resize()
      if (rainBounds) {
        mapInstance.fitBounds(rainBounds, { padding: 40, maxZoom: 10 })
      } else {
        mapInstance.setCenter(center)
        mapInstance.setZoom(zoom)
      }
    })

    // mapInstance.on('resize', () => {
    //   console.log('resize');
    //   mapInstance.setCenter(center);
    //   // mapInstance.resize();
    // });

    return () => {
      if (rainTimeout) clearTimeout(rainTimeout)
    }
  })
</script>

<div class="atom" bind:clientWidth={width}>
  <div id="graphicContainer">

    <div class="vis-chart-headline">{headline}</div>
    
    <div class="vis-chart-standfirst">{@html subtitle}</div>

    {#if scaleLabels.length > 0}
      <div class="rainfall-key" role="figure" aria-label="Rainfall scale">
        <span class="src-text-sans-15">Rainfall (mm)</span>
        <div
          class="rainfall-key-gradient"
          style="background: linear-gradient(to right, {keyGradientStyle})"
        ></div>
        <div class="rainfall-key-labels src-text-sans-15">
          {#each scaleLabels as label, i}
            <span
              class="rainfall-key-tick"
              style="left: {((i + 0.5) / scaleLabels.length) * 100}%"
            >
              {label}
            </span>
          {/each}
        </div>
      </div>
    {/if}

    <div
      id="cartoMap"
      class={MAP_INTERACTIVE ? "interactive" : "non-interactive"}
    >
      {#if isLoading}
        <div class="loading-overlay" role="status" aria-live="polite">
          <div class="spinner" aria-hidden="true"></div>
          <span class="loading-text">Loading map…</span>
        </div>
      {/if}
      {#if currentDate}
        <div class="date-badge" aria-live="polite">
          {new Date(currentDate + "T12:00:00").toLocaleDateString("en-AU", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })}
        </div>
      {/if}
      <div id="minimap"></div>
    </div>

    <div class="vis-chart-source">{@html source}</div>	

  </div>
</div>

<style lang="scss">
  :global(
      .non-interactive .maplibregl-canvas-container.maplibregl-interactive
    ) {
    cursor: default !important;
  }

  .atom {
    width: 100%;
    position: relative;

    .rainfall-key {
      display: flex;
      flex-direction: column;
      gap: 6px;
      margin-bottom: 12px;

      .rainfall-key-label {
        font-size: 12px;
        font-weight: 600;
        color: #333;
      }

      .rainfall-key-gradient {
        height: 12px;
        border-radius: 2px;
      }

      .rainfall-key-labels {
        position: relative;
        height: 16px;
        margin-top: 2px;
      }

      .rainfall-key-tick {
        position: absolute;
        transform: translateX(-50%);
        font-size: 11px;
        color: #666;
      }
    }

    #cartoMap {
      width: 100%;
      height: 500px;
      position: relative;

      .date-badge {
        position: absolute;
        top: 10px;
        right: 10px;
        z-index: 1000;
        padding: 6px 12px;
        background: rgba(255, 255, 255, 0.95);
        border-radius: 4px;
        font-size: 14px;
        font-weight: 600;
        color: #333;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
      }

      .loading-overlay {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        background: rgba(255, 255, 255, 0.8);
        z-index: 2000;

        .spinner {
          width: 20px;
          height: 20px;
          border: 2px solid #ccc;
          border-top-color: #333;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        .loading-text {
          font-size: 14px;
          color: #333;
        }
      }
    }

    :global(.maplibregl-ctrl-scale) {
      background: none;
      border-bottom: 2px solid #333;
      border-left: none;
      border-right: none;
      border-top: #333;
      box-sizing: border-box;
      color: #333;
      font-size: 10px;
      padding: 0 5px;
      white-space: nowrap;
    }

    #minimap {
      position: absolute;
      bottom: 0px;
      left: -15px;
      z-index: 1000;
    }
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
</style>
